import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { AnnotatedColumn } from '../../stores/reportBuilderStore';
import { Plus, Trash } from '@phosphor-icons/react';
import type { FilterOperator } from '../../types/reportBuilder';
import { OPERATOR_LABELS, RELATIVE_DATE_PRESETS } from '../../types/reportBuilder';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger,
} from '@t-one-internal/design-system';

const NO_VALUE_OPERATORS: FilterOperator[] = ['is_null', 'is_not_null'];

/** ISU-5: Check whether a filter row has a meaningful value. */
function isFilterValueEmpty(operator: FilterOperator, value: unknown): boolean {
  if (NO_VALUE_OPERATORS.includes(operator)) return false; // no value needed
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

export default function FilterBuilder() {
  const { selectedModel, selectedModelKeys, filters, getFilterableColumns, addFilter, removeFilter, updateFilter } = useReportBuilderStore();
  if (!selectedModel) return null;

  const filterableColumns = getFilterableColumns();
  const hasJoins = selectedModelKeys.length > 1;

  const handleAddFilter = () => {
    const firstCol = filterableColumns[0];
    if (!firstCol) return;
    addFilter({ column: firstCol.refKey, operator: firstCol.allowed_operators[0] || 'eq', value: '' });
  };

  const getCol = (refKey: string): AnnotatedColumn | undefined => filterableColumns.find(c => c.refKey === refKey);

  const handleColumnChange = (id: string, refKey: string) => {
    const col = getCol(refKey);
    updateFilter(id, { column: refKey, operator: col?.allowed_operators[0] || 'eq', value: '' });
  };

  const renderColumnItems = () => {
    if (!hasJoins) {
      return filterableColumns.map(c => <SelectItem key={c.refKey} value={c.refKey}>{c.label}</SelectItem>);
    }

    const groups: Record<string, AnnotatedColumn[]> = {};
    for (const col of filterableColumns) {
      const modelName = col.sourceModelName;
      if (!groups[modelName]) groups[modelName] = [];
      groups[modelName].push(col);
    }

    return Object.entries(groups).map(([modelName, cols]) => (
      <div key={modelName}>
        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">{modelName}</div>
        {cols.map(c => <SelectItem key={c.refKey} value={c.refKey}>{c.label}</SelectItem>)}
      </div>
    ));
  };

  const renderValueInput = (id: string, col: AnnotatedColumn | undefined, operator: FilterOperator, value: unknown) => {
    if (NO_VALUE_OPERATORS.includes(operator)) return null;

    if (operator === 'relative_date') {
      return (
        <Select value={String(value ?? '')} onValueChange={(v) => updateFilter(id, { value: v })}>
          <SelectTrigger className="h-7 w-full text-xs">
            <SelectValue placeholder="Select a preset..." />
          </SelectTrigger>
          <SelectContent>
            {RELATIVE_DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }

    if (operator === 'between') {
      const arr = Array.isArray(value) ? value : ['', ''];
      const type = col?.data_type === 'date' ? 'date' : col?.data_type === 'integer' || col?.data_type === 'decimal' ? 'number' : 'text';
      return (
        <div className="flex gap-1">
          <input type={type} value={String(arr[0] ?? '')} onChange={(e) => updateFilter(id, { value: [e.target.value, arr[1] ?? ''] })}
            className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="From" />
          <input type={type} value={String(arr[1] ?? '')} onChange={(e) => updateFilter(id, { value: [arr[0] ?? '', e.target.value] })}
            className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="To" />
        </div>
      );
    }

    if (operator === 'in' || operator === 'not_in') {
      if (col?.filter_options?.length) {
        const selected = Array.isArray(value) ? value as string[] : [];
        const label = selected.length === 0 ? 'Select values...' : `${selected.length} selected`;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex h-7 w-full items-center justify-between rounded-md border border-input bg-background px-2 text-xs ring-offset-background hover:bg-accent">
                <span className="truncate text-muted-foreground">{label}</span>
                <svg className="h-3 w-3 shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-h-48 overflow-y-auto" align="start">
              {col.filter_options.map(opt => (
                <DropdownMenuCheckboxItem
                  key={opt}
                  checked={selected.includes(opt)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) => {
                    const next = checked ? [...selected, opt] : selected.filter(v => v !== opt);
                    updateFilter(id, { value: next });
                  }}
                >
                  {opt}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
      return (
        <input type="text" value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
          onChange={(e) => updateFilter(id, { value: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
          className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="Comma-separated values" />
      );
    }

    if (col?.filter_input === 'select' && col.filter_options?.length && (operator === 'eq' || operator === 'ne')) {
      return (
        <Select value={String(value ?? '')} onValueChange={(v) => updateFilter(id, { value: v })}>
          <SelectTrigger className="h-7 w-full text-xs">
            <SelectValue placeholder="Select a value..." />
          </SelectTrigger>
          <SelectContent>
            {col.filter_options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }

    const inputType = col?.data_type === 'date' ? 'date' : col?.data_type === 'timestamp' ? 'datetime-local'
      : col?.data_type === 'integer' || col?.data_type === 'decimal' ? 'number' : 'text';

    return (
      <input type={inputType} value={String(value ?? '')}
        onChange={(e) => updateFilter(id, { value: inputType === 'number' ? (e.target.value ? Number(e.target.value) : '') : e.target.value })}
        className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none" placeholder="Value" />
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Filters</span>
        <button onClick={handleAddFilter} className="flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50">
          <Plus className="h-3 w-3" /> Add Filter
        </button>
      </div>

      {filters.map((f) => {
        const col = getCol(f.column);
        const empty = isFilterValueEmpty(f.operator, f.value);
        return (
          <div key={f.id} className={`space-y-1 rounded border p-2 ${empty ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center gap-1">
              <div className="flex-1">
                <Select value={f.column} onValueChange={(val) => handleColumnChange(f.id, val)}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {renderColumnItems()}
                  </SelectContent>
                </Select>
              </div>
              <Select value={f.operator} onValueChange={(val) => { const op = val as FilterOperator; updateFilter(f.id, { operator: op, value: NO_VALUE_OPERATORS.includes(op) ? null : '' }); }}>
                <SelectTrigger className="h-7 text-xs w-auto min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(col?.allowed_operators || []).map(op => <SelectItem key={op} value={op}>{OPERATOR_LABELS[op] || op}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => removeFilter(f.id)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500">
                <Trash className="h-3.5 w-3.5" />
              </button>
            </div>
            {renderValueInput(f.id, col, f.operator, f.value)}
            {empty && (
              <p className="text-[11px] text-amber-600">
                No value selected — this filter will be skipped.
              </p>
            )}
          </div>
        );
      })}
      {filters.length === 0 && <p className="text-xs text-gray-400 italic">No filters applied</p>}
    </div>
  );
}
